import { FieldType, FieldValue, FormField, FormSeparator, ModalId, O, useUIActions, ValidationModal2 } from "../../../../../features/ui"
import { PurchaseOrder, Supplier } from "../../../state/types"
import { Data2, DataTypeForm2 } from "../../../../../features/data-types"
import { useNavigate } from "react-router-dom"
import { useActions } from "./actions"
import { TableName } from "../../../../../tables"
import { useMemo, useState } from "react"
import { ItemValidation, OperationItemFormModal, PriceBreakdown, useOperationHooks, usePriceOutlineHooks, usePricesHooks } from "../../../../operations"
import { useManagerHooks } from "../../../../manager"
import { Product, useProductPricesLoaders } from "../../../../products"
import { SupplierField } from "../../supplier/supplier-field/supplierField"
import { PurchaseItemTable } from "../purchase-item-table/purchaseItemTable"
import { DeliveryData, DeliveryDataFields } from "../../../../deliveries"

type Props = {
    purchase?: PurchaseOrder
    supplier?: Supplier
}

export const PurchaseForm = (props: Props) => {
    const { purchase, supplier } = props
    const createMode = !purchase
    
    const { currentBranchId } = useManagerHooks()

    const defaultDeliveryData = useMemo(() => ({
        method: purchase?.deliveryData?.method,
        addressId: purchase?.deliveryData?.addressId,
        deliveryDate: purchase?.deliveryData?.deliveryDate
    }), [purchase?.deliveryData])

    const [supplierId, setSupplierId] = useState<O<string>>(supplier?.id)
    const [itemsData, setItemsData] = useState<Data2[]>(purchase?.items || [])
    const [currentItem, setCurrentItem] = useState<Data2>()
    const [deliveryData, setDeliveryData] = useState<Partial<DeliveryData>>(defaultDeliveryData)
    
    const { validateItems } = useOperationHooks()
    const { getPriceOutlineLive } = usePriceOutlineHooks()
    const pricesHooks = usePricesHooks()
    const navigate = useNavigate()

    const productPricesLoaders = useProductPricesLoaders()

    const { submitPurchase } = useActions()
    const { openModal } = useUIActions()

    const filterProducts = (product: Product) => product.supplierId === supplierId &&
        (currentItem?.productId === product.id || !itemsData.map(itemData => itemData.productId).includes(product.id))
    const openFormModal = () => openModal(ModalId.OPERATION_ITEM_FORM)
    const defaultDiscounts = async (productId: string) => {
        const productPrices = await productPricesLoaders().loadProductPrices(productId)
        return productPrices?.bonuses || []
    }

    const onChangeSupplier = (value?: FieldValue) => {
        setSupplierId(value as O<string>)
        setItemsData([])
    }

    const onCreateItem = () => {
        setCurrentItem(undefined)
        openFormModal()
    }

    const onEditItem = (productId: string) => {
        setCurrentItem(itemsData.find(itemData => itemData.productId === productId))
        openFormModal()
    }

    const onRemoveItem = (productId: string) => {
        const newItemsData = itemsData.filter(itemData => itemData.productId !== productId)
        setItemsData(newItemsData)
    }

    const fields: FormField[] = [
        {
            name: 'info-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Información" />
        },
        SupplierField({
            focus: true,
            value: supplierId,
            disabled: !createMode,
            narrow: true,
            onChange: onChangeSupplier
        }),
        {
            name: 'purchaseItems',
            type: FieldType.TABLE,
            render: () => (
                <PurchaseItemTable
                    itemsData={itemsData}
                    createMode={createMode}
                    supplierId={supplierId}
                    onCreateItem={onCreateItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                />
            )
        },
        {
            name: 'payment-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Pago" />
        },
        {
            name: 'priceBreakdown',
            type: FieldType.PRICE,
            label: 'Precio',
            render: () => {
                const priceOutline = getPriceOutlineLive(itemsData)
                return <PriceBreakdown
                    data={priceOutline}
                    discountLabel="Bonificaciones"
                />
            }
        }
    ]
    fields.splice(3, 0, ...DeliveryDataFields({
        table: TableName.PURCHASE_ORDERS,
        defaultData: deliveryData,
        addressIdValue: deliveryData.addressId,
        branchId: currentBranchId!,
        isDeliveryDateOptional: false,
        onChange: setDeliveryData
    }))

    const validate = async (): Promise<string[]> => {
        return validateItems(itemsData, [ItemValidation.NOT_EMPTY])
    }

    const onSubmit = async (data: Data2) => {
        await submitPurchase(
            data,
            currentBranchId!,
            itemsData,
            deliveryData,
            createMode
        )
    }

    const onCancel = () => navigate(-1)

    const onSaveItem = (newData: Data2) => {
        const matches = (data_1: Data2, data_2: Data2) => data_1.productId === data_2.productId
        const newItemsData = currentItem ?
            itemsData.map(itemData => matches(itemData, currentItem) ? newData : itemData) :
            itemsData.find(itemData => matches(itemData, newData)) ? itemsData : [...itemsData, newData]
        setItemsData(newItemsData)
    }

    return (
        <>
            <DataTypeForm2
               formId="purchase-form"
               fields={fields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
               validate={validate}
            />
            <OperationItemFormModal
                defaultData={currentItem}
                branchId={currentBranchId}
                priceLabel="Precio de Compra"
                discountLabel="Bonificación Especial"
                defaultPrice={pricesHooks.product.purchaseBasePrice}
                defaultDiscounts={defaultDiscounts}
                filterProducts={filterProducts}
                onSubmit={onSaveItem}
            />
            <ValidationModal2 title="Validaciones" />
        </>
    )
}
